import { cn } from '@shared/lib/cn'
import { cva, type VariantProps } from 'class-variance-authority'
import type * as React from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] cursor-pointer',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] disabled:bg-btn-disabled-bg disabled:text-btn-disabled-fg',
        primary:
          'bg-primary text-primary-foreground font-semibold hover:bg-primary/90 active:scale-[0.98] disabled:bg-btn-disabled-bg disabled:text-btn-disabled-fg',
        cta: 'bg-cta text-cta-foreground font-bold hover:bg-cta/90 active:scale-[0.98] disabled:bg-btn-disabled-bg disabled:text-btn-disabled-fg',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 disabled:bg-btn-disabled-bg disabled:text-btn-disabled-fg',
        outline:
          'border border-border bg-transparent text-foreground hover:bg-white/[0.04] active:scale-[0.98] disabled:opacity-40',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-white/[0.08] disabled:opacity-40',
        ghost:
          'hover:bg-white/[0.04] text-muted-foreground hover:text-foreground disabled:opacity-40',
        link: 'text-primary underline-offset-4 hover:underline disabled:opacity-40',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xs: 'h-7 gap-1 rounded-lg px-2.5 text-xs',
        sm: 'h-8 rounded-lg gap-1.5 px-3',
        lg: 'h-12 px-6 text-base rounded-xl',
        xl: 'h-14 px-8 text-base rounded-xl font-bold',
        icon: 'size-10',
        'icon-xs': 'size-7 rounded-lg',
        'icon-sm': 'size-8',
        'icon-lg': 'size-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? 'span' : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
